import { useState } from "react";

import {
    navigateToEntity,
    useTranslation,
    type CollectionActionsProps,
} from "@firecms/core";
import { AddIcon, Button, CircularProgress } from "@firecms/ui";
import type { Tag } from "@one-ai/shared/cms/schema";

import { todayIso } from "../collections/today";
import { collectionStringsFor } from "../i18n/collection-strings";
import { draftTagValues, nextNumericSlug } from "../lib/nextSlug";

/**
 * "New tag" — the only way to create one, and what makes tag slugs
 * auto-incrementing numbers.
 *
 * The tag counterpart of `NewArticleAction`, for the same mechanical reason:
 * the document id IS the slug, and FireCMS's create form insists on an id
 * **typed by an editor** (`EntityFullScreenRoute` passes `entityId: undefined`
 * whenever the URL carries `#new`, and `EntityEditView` derives its status from
 * exactly that). There is no hook for pre-filling it, so the number can only be
 * assigned by writing the document first, which is what this does.
 * `tagCollection` suppresses the stock Add button with
 * `permissions.create: false`.
 *
 * **What numbering costs a tag is different from what it costs an article, and
 * cheaper.** A tag's id is a public URL segment too — `/blog/tag/3` rather than
 * `/blog/tag/product-update` — so the slug stops carrying meaning and the
 * `label` becomes the only human-readable handle. Those pages are `noindex` and
 * out of the sitemap by design (see the route), so no readable URL was working
 * for search anyway; what is bought is the same thing as for articles — no typo
 * can become a permanent URL, and no editor has to invent a slug in two
 * languages.
 *
 * **Consequence, same as articles:** the record exists from the moment the
 * button is clicked. An editor who clicks and walks away leaves an empty tag in
 * the list — invisible everywhere public, because `draftTagValues` writes it
 * `retired: true`. Deleting the record is the whole cleanup.
 *
 * Numbering itself, and the two ways highest-plus-one can bite, are in
 * `lib/nextSlug.ts`.
 */
export function NewTagAction({
    path,
    collection,
    context,
}: CollectionActionsProps) {
    const { i18n } = useTranslation();
    const strings = collectionStringsFor(i18n.language).tag.newTag;
    const [creating, setCreating] = useState(false);

    const create = async () => {
        setCreating(true);
        try {
            // The whole collection, unfiltered: a retired tag still occupies
            // its number, and reusing it would point a new tag at a URL that
            // articles may still reference.
            const existing = await context.dataSource.fetchCollection<Tag>({
                path,
                collection,
            });
            // Ids alone — a tag has no `slug` field to disagree with its id.
            const slug = nextNumericSlug(existing.map((entity) => entity.id));

            // `context.dataSource`, deliberately not the collection's save
            // path: this write must NOT run the collection callbacks.
            // `onPreSave` would be harmless (the number passes its regex), but
            // `onSaveSuccess` triggers a site rebuild, and rebuilding for a
            // retired, unlabelled tag is a minute of CI for nothing. The real
            // save the editor makes next rebuilds normally.
            await context.dataSource.saveEntity<Tag>({
                path,
                entityId: slug,
                collection,
                status: "new",
                values: draftTagValues({ date: todayIso() }),
            });

            navigateToEntity({
                // Tags are edited in the side panel — `tagCollection` sets no
                // `openEntityMode`, and unlike an article a tag is four fields
                // rather than a page.
                openEntityMode: collection.openEntityMode ?? "side_panel",
                collection,
                entityId: slug,
                path,
                sideEntityController: context.sideEntityController,
                navigation: context.navigation,
            });
        } catch (error) {
            console.error(`[${path}] could not create a tag`, error);
            context.snackbarController.open({
                type: "error",
                message: strings.failed,
            });
        } finally {
            setCreating(false);
        }
    };

    return (
        <Button
            onClick={create}
            disabled={creating}
            variant="filled"
            color="primary"
            size="small"
            startIcon={
                creating ? (
                    <CircularProgress size="smallest" />
                ) : (
                    <AddIcon size="small" />
                )
            }
        >
            {strings.button}
        </Button>
    );
}
